import { useState } from "react";
import { FileText, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { AppDialog } from "@/components/ui/app-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/components/auth-provider";
import { signLease } from "@/lib/lease-service";
import { LeaseAgreement } from "@/types/types";
import { formatNaira } from "@/lib/utils";

interface LeaseSigningDialogProps {
  lease: LeaseAgreement;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function LeaseSigningDialog({
  lease,
  open,
  onOpenChange,
  onSuccess,
}: LeaseSigningDialogProps) {
  const { user } = useAuth();
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isSigning, setIsSigning] = useState(false);

  const isLandlord = user?.id === lease.landlordId;
  const alreadySigned = isLandlord ? lease.landlordSigned : lease.tenantSigned;

  const handleSign = async () => {
    if (!user?.id) {
      toast.error("You must be signed in to sign the lease");
      return;
    }

    if (!hasReadTerms || !agreeToTerms) {
      toast.error("Please confirm that you have read and agree to the terms");
      return;
    }

    setIsSigning(true);

    try {
      console.log("Signing lease:", {
        leaseId: lease.id,
        userId: user.id,
        isLandlord: user.id === lease.landlordId,
        isTenant: user.id === lease.tenantId,
        tenantId: lease.tenantId,
        landlordId: lease.landlordId,
      });

      // Generate a simple signature (in production, use a proper e-signature solution)
      const signature = `${user.name} - ${new Date().toISOString()}`;

      await signLease(lease.id, user.id, signature);

      toast.success("Lease signed successfully!", {
        description:
          lease.landlordSigned && lease.tenantSigned
            ? "The lease is now active"
            : "Waiting for the other party to sign",
      });

      setHasReadTerms(false);
      setAgreeToTerms(false);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Error signing lease:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to sign lease"
      );
    } finally {
      setIsSigning(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Lease Agreement"
      description={
        alreadySigned
          ? "You have already signed this lease"
          : "Please review and sign the lease agreement"
      }
      icon={<FileText className="h-5 w-5" />}
      maxWidth="4xl"
      footer={
        <DialogFooter className="flex-col sm:flex-row gap-3">
          <div className="flex items-start gap-2 flex-1">
            <Checkbox
              id="read-terms"
              checked={hasReadTerms}
              onCheckedChange={(checked) => setHasReadTerms(checked === true)}
              disabled={alreadySigned}
            />
            <Label
              htmlFor="read-terms"
              className="text-sm font-normal cursor-pointer"
            >
              I have read and understood all terms of this lease agreement
            </Label>
          </div>
          <div className="flex items-start gap-2 flex-1">
            <Checkbox
              id="agree-terms"
              checked={agreeToTerms}
              onCheckedChange={(checked) => setAgreeToTerms(checked === true)}
              disabled={alreadySigned}
            />
            <Label
              htmlFor="agree-terms"
              className="text-sm font-normal cursor-pointer"
            >
              I agree to be bound by these terms and conditions
            </Label>
          </div>
          <Button
            onClick={handleSign}
            disabled={
              !hasReadTerms || !agreeToTerms || alreadySigned || isSigning
            }
            className="w-full sm:w-auto"
          >
            {isSigning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing...
              </>
            ) : alreadySigned ? (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                Signed
              </>
            ) : (
              "Sign Lease"
            )}
          </Button>
        </DialogFooter>
      }
    >
      <>
        {/* Lease Summary */}
        <div className="border rounded-lg p-4 bg-muted/50">
          <h3 className="font-semibold mb-3">Lease Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-muted-foreground">Duration</p>
              <p className="font-medium">
                {formatDate(lease.startDate)} - {formatDate(lease.endDate)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Monthly Rent</p>
              <p className="font-medium">{formatNaira(lease.monthlyRent)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Security Deposit</p>
              <p className="font-medium">
                {formatNaira(lease.securityDeposit)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Payment Due</p>
              <p className="font-medium">
                Day {lease.paymentDueDay} of each month
              </p>
            </div>
          </div>
        </div>

        {/* Terms and Conditions */}
        <div>
          <h3 className="font-semibold mb-3">Terms and Conditions</h3>

          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Tenant Responsibilities</h4>
              <ul className="list-disc pl-5 space-y-1">
                {lease.tenantResponsibilities.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-medium mb-2">Landlord Responsibilities</h4>
              <ul className="list-disc pl-5 space-y-1">
                {lease.landlordResponsibilities.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-medium mb-2">Property Rules</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Pets: {lease.petsAllowed ? "Allowed" : "Not allowed"}</li>
                <li>
                  Smoking: {lease.smokingAllowed ? "Allowed" : "Not allowed"}
                </li>
                <li>
                  Subletting:{" "}
                  {lease.sublettingAllowed ? "Allowed" : "Not allowed"}
                </li>
                <li>Maximum occupants: {lease.maxOccupants}</li>
              </ul>
            </div>

            {lease.utilitiesIncluded.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Utilities Included</h4>
                <ul className="list-disc pl-5 space-y-1">
                  {lease.utilitiesIncluded.map((utility, index) => (
                    <li key={index} className="capitalize">
                      {utility}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <h4 className="font-medium mb-2">Late Fees</h4>
              <p>
                A late fee of {formatNaira(lease.lateFeeAmount)} will be charged
                if rent is not paid within {lease.lateFeeGracePeriod} days of
                the due date.
              </p>
            </div>

            <div>
              <h4 className="font-medium mb-2">Termination</h4>
              <p>
                Either party may terminate this lease with{" "}
                {lease.terminationNotice} days written notice.
              </p>
            </div>

            <div>
              <h4 className="font-medium mb-2">Renewal</h4>
              <p>{lease.renewalTerms}</p>
            </div>
          </div>
        </div>

        {/* Signature Status */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold mb-3">Signatures</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {lease.landlordSigned ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
              )}
              <span>
                Landlord{" "}
                {lease.landlordSigned &&
                  `(Signed on ${formatDate(lease.landlordSignedAt || "")})`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {lease.tenantSigned ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
              )}
              <span>
                Tenant{" "}
                {lease.tenantSigned &&
                  `(Signed on ${formatDate(lease.tenantSignedAt || "")})`}
              </span>
            </div>
          </div>
        </div>
      </>
    </AppDialog>
  );
}
